// src/components/profile/theme/ThemePreview.tsx
import React from "react";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import HomeIcon from "@mui/icons-material/Home";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ViewListIcon from "@mui/icons-material/ViewList";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { Theme } from "../types/profile";
import CommonButtons from "../../../components/CommonButtons";
import { useUserTheme } from "src/shared/hooks/useUserTheme";

interface ThemePreviewProps {
  theme: Theme;
  logo: string;
  userName: string;
  businessName: string;
}

const ICONS = [
  { Component: HomeIcon, name: "Home" },
  { Component: DashboardRoundedIcon, name: "Dashboard" },
  { Component: ViewListIcon, name: "Orders" },
  { Component: ImportContactsIcon, name: "Ledger" },
  { Component: SettingsSuggestIcon, name: "Settings" },
];

const ThemePreview: React.FC<ThemePreviewProps> = ({ theme, logo, userName, businessName }) => {
  // Use passed prop theme values so live color/font updates reflect instantly
  const { bgClass, fontClass} = useUserTheme();

  return (
    <div className="w-full flex justify-center items-center">
      <div className={`w-full max-w-[340px] sm:max-w-[360px] border border-gray-300 rounded-lg overflow-hidden shadow-md bg-white ${bgClass} ${fontClass}`}>
        {/* Mock Header */}
        <header className={`flex justify-between items-center px-2.5 py-2 transition-colors duration-200 ${bgClass}`}>
          <div className="flex items-center space-x-1.5 truncate pr-2">
            {logo && <img src={logo} alt="Logo" className="w-5 h-5 rounded-full object-cover shrink-0" />}
            <p className="font-semibold text-xs truncate">{businessName || "Business Name"}</p>
          </div>
          <div className="flex items-center bg-white/25 rounded px-1.5 py-0.5 text-[11px] shrink-0">
            <AccountCircleIcon className="mr-0.5 !text-sm" />
            <span className="max-w-[70px] truncate">{userName || "User"}</span>
            <ArrowDropDownIcon className="!text-sm" />
          </div>
        </header>

        {/* Mock Workspace Content */}
        <div className="flex h-48 sm:h-52">
          {/* Mock Navigation Sidebar */}
          <div className="w-10 sm:w-12 bg-gray-50 border-r border-gray-200 flex flex-col items-center py-2 space-y-2 shrink-0">
            {ICONS.map(({ Component, name }) => (
              <Component key={name} className={`cursor-pointer p-1 rounded hover:bg-gray-200 ${bgClass} ${fontClass}`} />
            ))}
          </div>

          {/* Mock Body View */}
          <div className="flex-1 p-2.5 flex flex-col justify-between bg-gray-50/50">
            <div className="space-y-1.5 bg-white p-2.5 rounded border border-gray-200 shadow-2xs">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Font Theme</p>
              <p className="text-xs text-gray-800">
                Selected: <span className="font-semibold">{theme?.fontFamily}</span>
              </p>
              <p className="text-[11px] text-gray-500 leading-tight">This is a live preview of how your typography will look inside application cards.</p>
            </div>

            <div className="flex gap-1.5 justify-end pt-1">
              <CommonButtons label="CANCEL" variant="outlineDanger" className="h-8" />
              <CommonButtons label="CONFIRM" className="h-8" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePreview;
